import { LockIcon } from "@otrium/icons";
import { ConditionalLink } from "src/atoms/ConditionalLink";
import { theme } from "src/theme";
import type { Suggestion } from "src/types/adapters/query.type";
import { SuggestionItemWrapper } from "../SearchResults.styled";
import { capitalize } from "src/utils/helper/string";
import { useLingui } from "@lingui/react";
import { getSuggestionItemLink } from "../utils";
import { locale } from "src/config/app-config";
import { useCallback, useContext, useMemo } from "react";
import { SearchResultsDispatchContext } from "../search-results.context";
import { useShopTypeContext } from "src/hooks/context/use-shop-type-context";
import { Breadcrumb } from "src/organisms/Breadcrumb";
import {
  GATED_POPUP_REDIRECT_DATA,
  GATED_POPUP_SOURCE,
  GATED_SEARCH_SUGGESTION_ITEM,
} from "src/constants/gatedItem";
import {
  deleteSessionStorageItem,
  setSessionStorageJSON,
} from "src/utils/helper/sessionStorage";
import { useNewIsLoggedIn } from "src/hooks/useIsLoggedIn";

type SuggestionProps = {
  suggestion: Suggestion;
  position: number;
};

function SuggestionItem({ suggestion, position }: SuggestionProps) {
  const { onSuggestionClick } = useContext(SearchResultsDispatchContext);

  const { i18n } = useLingui();
  const { currentShopType } = useShopTypeContext();
  const { isLoggedIn } = useNewIsLoggedIn();
  const parts = suggestion.title.split(/(<em>.*?<\/em>)/g);
  const suggestionHref = getSuggestionItemLink({
    suggestion,
    locale,
    shopType: currentShopType,
  });

  const isGated = useMemo(
    () => suggestion.isGated && !isLoggedIn,
    [suggestion.isGated, isLoggedIn]
  );

  const href = isGated
    ? `/sign-in?redirect_to=${suggestionHref}&source=${GATED_POPUP_SOURCE}`
    : suggestionHref;

  const handleClick = useCallback(() => {
    onSuggestionClick({
      gateLevel: suggestion.gateLevel,
      itemId: suggestion.id,
      position,
      type: suggestion.type,
    });
  }, [
    onSuggestionClick,
    suggestion.gateLevel,
    suggestion.id,
    suggestion.type,
    position,
  ]);

  const handleGatedSuggestionClick = useCallback(() => {
    deleteSessionStorageItem(GATED_POPUP_REDIRECT_DATA);
    setSessionStorageJSON(GATED_POPUP_REDIRECT_DATA, {
      gatedActionType: "Login",
      actionSource: GATED_SEARCH_SUGGESTION_ITEM,
      brandName: suggestion.title,
      shopTypeFromRedirect: currentShopType,
      gateLevel: suggestion.gateLevel,
    });
  }, [currentShopType, suggestion.gateLevel, suggestion.title]);

  return (
    <SuggestionItemWrapper
      data-cnstrc-item-section="Search Suggestions"
      data-cnstrc-item-id={suggestion.id}
      data-cnstrc-item-name={suggestion.title}
    >
      <ConditionalLink
        href={href}
        condition={Boolean(href)}
        onClick={isGated ? handleGatedSuggestionClick : handleClick}
      >
        {parts.map((part, i) => {
          if (part.startsWith("<em>") && part.endsWith("</em>")) {
            const content = part.replace(/<\/?em>/g, "");
            return <b key={i}>{content}</b>;
          }

          return <span key={i}>{part}</span>;
        })}

        <span className="suggestion-item-type">
          {` - ${i18n._(capitalize(suggestion.type))}`}
        </span>

        {isGated && (
          <LockIcon
            color={theme.colors.tone.black}
            width={22}
            height={16}
            style={{
              paddingLeft: "6px",
            }}
          />
        )}

        {suggestion.breadcrumb ? (
          <Breadcrumb
            data={suggestion.breadcrumb}
            separator="circle"
            color={theme.colors.tone.anthracite}
            fontSize="fontSize16"
            gap="space8"
          />
        ) : null}
      </ConditionalLink>
    </SuggestionItemWrapper>
  );
}

export { SuggestionItem };
